/**
 * Report Section Editor Card - Isolated content state for production performance
 *
 * Owns section content state so typing does not re-render the entire ReportEditor.
 * Exposes getContent() via ref so parent can read latest content on Save.
 * Calls onContentChange debounced so parent can update word count / hasChanges without re-rendering every keystroke.
 */

import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { RotateCcw } from 'lucide-react';

export interface ReportSectionEditorCardProps {
  sectionTitle: string;
  initialContent: string;
  sectionIndex: number;
  onRevertSection: (index: number) => void;
  onContentChange?: (index: number, content: string) => void;
  debounceMs?: number;
}

export interface ReportSectionEditorCardRef {
  getContent: () => string;
}

export const ReportSectionEditorCard = forwardRef<ReportSectionEditorCardRef, ReportSectionEditorCardProps>(
  function ReportSectionEditorCard(
    {
      sectionTitle,
      initialContent,
      sectionIndex,
      onRevertSection,
      onContentChange,
      debounceMs = 400,
    },
    ref
  ) {
    const [localContent, setLocalContent] = useState<string>(initialContent);
    const latestContentRef = useRef<string>(initialContent);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync from parent when initialContent changes (e.g. revert section or load)
    useEffect(() => {
      setLocalContent(initialContent);
      latestContentRef.current = initialContent;
    }, [initialContent]);

    // Keep ref in sync and debounce parent callback
    useEffect(() => {
      latestContentRef.current = localContent;

      if (onContentChange == null) return;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        onContentChange(sectionIndex, localContent);
      }, debounceMs);

      return () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      };
    }, [localContent, sectionIndex, onContentChange, debounceMs]);

    useImperativeHandle(
      ref,
      () => ({
        getContent: () => latestContentRef.current,
      }),
      []
    );

    const hasChanges = localContent !== initialContent;
    const wordCount = localContent.split(/\s+/).filter(w => w.length > 0).length;
    const citationCount = Math.max(0, localContent.split(/\[Source \d+\]/).length - 1);
    const lineCount = localContent.split('\n').length;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            {sectionTitle}
            {hasChanges && (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs font-medium">
                Modified
              </span>
            )}
          </h3>
          <button
            onClick={() => onRevertSection(sectionIndex)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <RotateCcw className="h-3 w-3" />
            Revert Section
          </button>
        </div>

        <textarea
          value={localContent}
          onChange={(e) => setLocalContent(e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg font-mono text-sm min-h-[200px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            hasChanges ? 'border-yellow-300 bg-yellow-50/30' : 'border-gray-300'
          }`}
          spellCheck={false}
        />

        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{citationCount} Citation(s)</span>
          <div className="flex items-center gap-3">
            <span>{wordCount} words</span>
            <span>{lineCount} Lines</span>
          </div>
        </div>
      </div>
    );
  }
);
